<script setup lang="ts">
/**
 * Fair Home V2 hero — Figma RedisignHomePage `15:9503` / compact `15:9523`,
 * layered as in `design/fair-rail-sources/demo-design.html` (HeroExample):
 * page bg → giant FAIR wordmark (z1) → interactive rail (z2, transparent,
 * `position:absolute; inset:0`) → taglines (z3).
 * The rail is the black-box runtime `public/fair-rail/` mounted through
 * `hero/FairRail.vue`; the only tuning knobs are `rackWidth` / `tubeAt`.
 * Tagline motion is the Figma animation screen (infinite 24.971428s loop,
 * per-node keyframes in css).
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import fairLogoWordmarkUrl from '@/ui/icons/brand/fair-logo-wordmark.svg'
import FairRail, { type FairRailInstance } from './hero/FairRail.vue'
import { useAiraTouchGuard } from '@/features/aira-widget'
import { useI18n } from 'vue-i18n'
import {
  FAIR_HOME_V2_HERO_TAGLINES,
  type FairHomeV2HeroTaglineAnchor,
} from './fair-home-v2-hero.data'
import './fair-home-v2-hero.css'

interface FairHomeV2HeroProps {
  compact?: boolean
  /** the wordmark is mid-flight to the header, or already living there (FE-002) */
  wordmarkHidden?: boolean
}

const props = withDefaults(defineProps<FairHomeV2HeroProps>(), {
  compact: false,
  wordmarkHidden: false,
})

const emit = defineEmits<{
  /** the rail is fully dressed — the page loader may hand the screen over */
  railLoad: []
}>()

/**
 * Fit is tuned ONLY through rackWidth / tubeAt (INTEGRATION.md). Values are
 * derived from Figma `15:9505` (scene rect x0 y275 w1440 → scale 1440/2752)
 * + manifest: rack 378–2327 → 0.708 of hero width; tube y324.5 → 275 + 170 =
 * 445 of 900 → 0.494. Floor lands at 1051 → legs run off the bottom edge,
 * wordmark (y354) sits above the bar with garments in front of its lower half.
 */
const RAIL_OPTIONS_DESKTOP = {
  fit: 'design' as const,
  rackWidth: 0.708,
  tubeAt: 0.494,
  background: 'transparent',
}

/**
 * Compact — Figma `15:9526` scene rect x-253 y402 w899 on the 393×852 frame
 * → scale 899/2752: rack 1949·0.3267 = 637 frame-px (wider than the 393
 * frame); tube 402 + 106 = 508 of 852 → 0.596. Floor at 886 → legs run off
 * the bottom edge.
 *
 * The compact rail host spans the FULL hero width (so nothing is cropped in
 * the letterbox on tablets), while the rack must stay 637u wide. rackWidth is
 * a fraction of the host width, so it is derived from the measured hero size:
 * rackWidth = 637 · u / heroWidth, u = min(heroWidth / 393, heroHeight / 852).
 */
const COMPACT_FRAME = { width: 393, height: 852 }
const COMPACT_RACK_FRAME_PX = 637

const heroRef = ref<HTMLElement | null>(null)
const heroWidth = ref(COMPACT_FRAME.width)
const heroHeight = ref(COMPACT_FRAME.height)
let heroObserver: ResizeObserver | null = null

onMounted(() => {
  if (!heroRef.value || typeof ResizeObserver === 'undefined') return
  heroObserver = new ResizeObserver((entries) => {
    const rect = entries[0]?.contentRect
    if (!rect) return
    heroWidth.value = rect.width
    heroHeight.value = rect.height
  })
  heroObserver.observe(heroRef.value)
})

onBeforeUnmount(() => {
  heroObserver?.disconnect()
  heroObserver = null
})

const compactRackWidth = computed(() => {
  const w = heroWidth.value
  const h = heroHeight.value
  if (!(w > 0) || !(h > 0)) return COMPACT_RACK_FRAME_PX / COMPACT_FRAME.width
  const unit = Math.min(w / COMPACT_FRAME.width, h / COMPACT_FRAME.height)
  return Math.round(((COMPACT_RACK_FRAME_PX * unit) / w) * 1000) / 1000
})

const railOptions = computed(() =>
  props.compact
    ? {
        fit: 'design' as const,
        rackWidth: compactRackWidth.value,
        tubeAt: 0.596,
        background: 'transparent',
      }
    : RAIL_OPTIONS_DESKTOP,
)

/**
 * `railOptions` is applied live via `rail.setOptions()` (see hero/FairRail.vue), so the
 * rail is mounted exactly once per page visit. It used to be re-keyed on every change of
 * `compactRackWidth` — i.e. on orientation change and on every mobile URL-bar collapse —
 * which tore down the WebGL context and re-decoded all garment textures each time.
 */
const railRef = ref<InstanceType<typeof FairRail> | null>(null)
const { t } = useI18n()

/**
 * Play with the rack for too long and Aira comes over to defend her wardrobe.
 * The rail renders a single host element, so its DOM node is the component's `$el`.
 */
function resolveRailElement(): HTMLElement | null {
  const fromRef = (railRef.value as { $el?: unknown } | null)?.$el
  if (fromRef instanceof HTMLElement) return fromRef
  // `defineExpose` in FairRail can hide `$el` behind the exposed proxy, so fall back
  // to the class this component puts on the rail's host node.
  return document.querySelector<HTMLElement>('.fair-home-v2-hero__rail')
}

useAiraTouchGuard(resolveRailElement, {
  lines: [
    t('aira_hero.stop_touching_1'),
    t('aira_hero.stop_touching_2'),
    t('aira_hero.stop_touching_3'),
  ],
  finaleAt: 4,
  finalLine: t('aira_hero.enough'),
})

function handleRailReady(rail: FairRailInstance) {
  setTimeout(() => rail.nudge(null, 0.5), 600)
}

function handleRailLoad() {
  emit('railLoad')
}

function anchorOf(index: number): FairHomeV2HeroTaglineAnchor {
  const tagline = FAIR_HOME_V2_HERO_TAGLINES[index]
  return props.compact ? tagline.compact : tagline.desktop
}

/** Figma px → design units (`--fair-hero-u`, 1440×900 desktop / 393×852 compact). */
function u(value: string): string {
  const n = Number.parseFloat(value)
  return Number.isFinite(n) ? `calc(${n} * var(--fair-hero-u))` : value
}

function taglineStyle(anchor: FairHomeV2HeroTaglineAnchor, index: number): Record<string, string> {
  const style: Record<string, string> = {
    top: u(anchor.top),
    width: u(anchor.width),
    textAlign: anchor.align === 'end' ? 'right' : 'left',
    '--fair-home-v2-hero-tagline-index': String(index),
  }
  if (anchor.left !== undefined) style.left = u(anchor.left)
  if (anchor.right !== undefined) style.right = u(anchor.right)
  return style
}
</script>

<template>
  <div
    ref="heroRef"
    class="fair-home-v2-hero"
    :class="{ 'fair-home-v2-hero--compact': compact }"
    aria-label="Hero"
  >
    <div
      class="fair-home-v2-hero__bg"
      aria-hidden="true"
    />

    <!-- Rail — sibling of the canvas (no stacking context in between): z2 between
         wordmark z1 and taglines z3. Desktop host = canvas box; compact host =
         full hero width × 852u so the wide rack is never cropped by the canvas. -->
    <FairRail
      ref="railRef"
      class="fair-home-v2-hero__rail"
      assets="/fair-rail/"
      :options="railOptions"
      aria-hidden="true"
      @ready="handleRailReady"
      @load="handleRailLoad"
    />

    <!-- design canvas (contain-fit, bottom-pinned) — wordmark z1 → taglines z3 -->
    <div class="fair-home-v2-hero__canvas">
      <div
        class="fair-home-v2-hero__wordmark"
        :class="{ 'fair-home-v2-hero__wordmark--handed-over': wordmarkHidden }"
        aria-hidden="true"
      >
        <img
          class="fair-home-v2-hero__wordmark-img"
          :src="fairLogoWordmarkUrl"
          alt=""
          width="884"
          height="192"
          decoding="async"
        >
      </div>

      <ul
        class="fair-home-v2-hero__taglines"
        aria-label="FAIR taglines"
      >
        <li
          v-for="(tagline, index) in FAIR_HOME_V2_HERO_TAGLINES"
          :key="tagline.id"
          class="fair-home-v2-hero__tagline"
          :class="`fair-home-v2-hero__tagline--${tagline.id}`"
          :style="taglineStyle(anchorOf(index), index)"
        >
          <p
            v-for="(line, lineIndex) in tagline.lines"
            :key="lineIndex"
            class="fair-home-v2-hero__tagline-line"
          >
            {{ line }}
          </p>
        </li>
      </ul>
    </div>
  </div>
</template>
